import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  IconButton,
  LinearProgress,
  Rating,
  Paper,
  Tooltip,
  Menu,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Add,
  Visibility,
  FilterList,
  Download,
  Build,
  GpsFixed,
  Security,
  History,
  MoreVert,
  Description,
  CheckCircle,
  Schedule,
  Cancel,
  Edit,
  Delete,
} from '@mui/icons-material';
import { getServiceIcon, getServiceColor } from '@/utils/serviceIconUtils';
import {
  partnerService,
  Partner,
  Upload,
  Review,
  UploadStats,
  CreatePartnerData,
  UpdatePartnerData,
  CreateUploadData
} from '@/services/partnerService';
import { AddEditPartnerDialog } from './AddEditPartnerDialog';
import { UploadDocumentDialog } from './UploadDocumentDialog';
import { PartnerFilterDialog, PartnerFilters } from './PartnerFilterDialog';
import { PartnerDetailDialog } from './PartnerDetailDialog';
import { FleetPartnersTab } from './FleetPartnersTab';

export const PartnerManagementPage: React.FC = () => {
  // Tab & Menu State
  const [selectedTab, setSelectedTab] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPartnerId, setSelectedPartnerId] = useState<string | null>(null);

  // Data State
  const [partners, setPartners] = useState<Partner[]>([]);
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [uploadStats, setUploadStats] = useState<UploadStats | null>(null);

  // Loading State
  const [loading, setLoading] = useState(false);
  const [uploadsLoading, setUploadsLoading] = useState(false);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Dialog State
  const [addEditDialogOpen, setAddEditDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState<Partner | null>(null);
  const [viewingPartner, setViewingPartner] = useState<Partner | null>(null);

  // Filter State
  const [filters, setFilters] = useState<PartnerFilters>({});

  // Notification State
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  // Load Partners
  const loadPartners = async () => {
    setLoading(true);
    try {
      // Skip loading if on Fleet Partners tab (tab 1) - it has its own data loading
      if (selectedTab === 1) {
        setLoading(false);
        return;
      }

      const serviceTypeFilter: 'Inspection' | 'GPS' | 'Documentation' | 'Insurance' | undefined =
        selectedTab === 2 ? 'Inspection' :
        selectedTab === 3 ? 'Inspection' :
        selectedTab === 4 ? 'GPS' :
        undefined;

      // Clean filters - remove empty strings and build proper filter object
      const apiFilters: {
        serviceType?: 'Inspection' | 'GPS' | 'Documentation' | 'Insurance';
        status?: 'active' | 'inactive' | 'suspended';
        minRating?: number;
        search?: string;
      } = {};

      if (filters.search) apiFilters.search = filters.search;
      if (filters.minRating) apiFilters.minRating = filters.minRating;
      // Only include status if it's one of the valid values (not empty string)
      const validStatus = filters.status as string;
      if (validStatus && validStatus !== '') {
        apiFilters.status = validStatus as 'active' | 'inactive' | 'suspended';
      }
      if (serviceTypeFilter) apiFilters.serviceType = serviceTypeFilter;

      const response = await partnerService.getPartners(apiFilters);

      if (response.success && response.data) {
        setPartners(response.data.partners);
      } else {
        showSnackbar(response.error || 'Failed to load partners', 'error');
      }
    } catch (error) {
      console.error('Error loading partners:', error);
      showSnackbar('Failed to load partners', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Load Uploads
  const loadUploads = async () => {
    setUploadsLoading(true);
    try {
      const response = await partnerService.getUploads({ limit: 10 });
      if (response.success && response.data) {
        setUploads(response.data.uploads);
      }
    } catch (error) {
      console.error('Error loading uploads:', error);
    } finally {
      setUploadsLoading(false);
    }
  };

  // Load Reviews
  const loadReviews = async () => {
    setReviewsLoading(true);
    try {
      const response = await partnerService.getReviews({ limit: 10 });
      if (response.success && response.data) {
        setReviews(response.data.reviews);
      }
    } catch (error) {
      console.error('Error loading reviews:', error);
    } finally {
      setReviewsLoading(false);
    }
  };

  // Load Upload Stats
  const loadUploadStats = async () => {
    try {
      const response = await partnerService.getUploadStats();
      if (response.success && response.data) {
        setUploadStats(response.data);
      }
    } catch (error) {
      console.error('Error loading upload stats:', error);
    }
  };

  // Initial Load
  useEffect(() => {
    loadPartners();
    loadUploads();
    loadReviews();
    loadUploadStats();
  }, []);

  // Reload on filter or tab change
  useEffect(() => {
    loadPartners();
  }, [filters, selectedTab]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Verified': return 'success';
      case 'Pending Review': return 'warning';
      case 'Rejected': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Verified': return <CheckCircle />;
      case 'Pending Review': return <Schedule />;
      case 'Rejected': return <Cancel />;
      default: return <Schedule />;
    }
  };

  // Snackbar Helper
  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Menu Handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, partnerId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedPartnerId(partnerId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPartnerId(null);
  };

  // Partner CRUD Handlers
  const handleAddPartner = () => {
    setEditingPartner(null);
    setAddEditDialogOpen(true);
  };

  const handleEditPartner = (partnerId: string) => {
    const partner = partners.find(p => p.id === partnerId);
    if (partner) {
      setEditingPartner(partner);
      setAddEditDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleViewPartner = (partnerId: string) => {
    const partner = partners.find(p => p.id === partnerId);
    if (partner) {
      setViewingPartner(partner);
      setDetailDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleSavePartner = async (data: CreatePartnerData | UpdatePartnerData) => {
    setActionLoading(true);
    try {
      let response;
      if (editingPartner) {
        response = await partnerService.updatePartner(editingPartner.id, data);
        if (response.success) {
          showSnackbar('Partner updated successfully', 'success');
        }
      } else {
        response = await partnerService.createPartner(data as CreatePartnerData);
        if (response.success) {
          showSnackbar('Partner created successfully', 'success');
        }
      }

      if (response.success) {
        loadPartners();
        setAddEditDialogOpen(false);
      } else {
        console.error('❌ Operation failed:', response.error);
        showSnackbar(response.error || 'Failed to save partner', 'error');
        throw new Error(response.error || 'Failed to save partner');
      }
    } catch (error) {
      console.error('❌ Exception in handleSavePartner:', error);
      showSnackbar(error instanceof Error ? error.message : 'Failed to save partner', 'error');
      throw error; // Re-throw so the dialog can handle it
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeletePartner = async (partnerId: string) => {
    const partner = partners.find(p => p.id === partnerId);
    if (!partner) return;

    // Confirm deletion
    if (!window.confirm(`Are you sure you want to delete "${partner.name}"? This action cannot be undone.`)) {
      handleMenuClose();
      return;
    }

    setActionLoading(true);
    try {
      const response = await partnerService.deletePartner(partnerId);

      if (response.success) {
        showSnackbar('Partner deleted successfully', 'success');
        loadPartners();
      } else {
        showSnackbar(response.error || 'Failed to delete partner', 'error');
      }
    } catch (error) {
      console.error('❌ Exception in handleDeletePartner:', error);
      showSnackbar(error instanceof Error ? error.message : 'Failed to delete partner', 'error');
    } finally {
      setActionLoading(false);
      handleMenuClose();
    }
  };

  // Upload Handlers
  const handleUploadDocument = () => {
    setUploadDialogOpen(true);
  };

  const handleSaveUpload = async (data: CreateUploadData) => {
    setActionLoading(true);
    try {
      const response = await partnerService.createUpload(data);
      if (response.success) {
        showSnackbar('Document uploaded successfully', 'success');
        loadUploads();
        loadUploadStats();
        setUploadDialogOpen(false);
      } else {
        showSnackbar(response.error || 'Failed to upload document', 'error');
      }
    } catch (error) {
      showSnackbar('Failed to upload document', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // Filter Handlers
  const handleFilterPartners = () => {
    setFilterDialogOpen(true);
  };

  const handleApplyFilters = (newFilters: PartnerFilters) => {
    setFilters(newFilters);
  };

  // Export Handler
  const handleExportPartners = () => {
    partnerService.exportPartnersToCSV(partners);
    showSnackbar('Partners exported successfully', 'success');
  };

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            Partner Management Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage garages, inspectors, and service partners
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            sx={{ textTransform: 'none' }}
            onClick={handleFilterPartners}
            data-testid="filter-partners-btn"
          >
            Filters
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download />}
            sx={{ textTransform: 'none' }}
            onClick={handleExportPartners}
            data-testid="export-partners-btn"
          >
            Export
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            sx={{ textTransform: 'none' }}
            onClick={handleAddPartner}
            data-testid="add-partner-btn"
          >
            Add Partner
          </Button>
        </Box>
      </Box>

      {/* Partner Type Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={selectedTab} onChange={(_, newValue) => setSelectedTab(newValue)}>
          <Tab label="Service Partners" />
          <Tab label="Fleet Partners ⭐" />
          <Tab label="Garages" />
          <Tab label="Inspectors" />
          <Tab label="GPS Installers" />
        </Tabs>
      </Box>

      {/* Fleet Partners Tab Content */}
      {selectedTab === 1 && (
        <FleetPartnersTab />
      )}

      {/* Service Partners Tab Content */}
      {selectedTab !== 1 && (
        <>
          {/* Loading State */}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Upload Portal */}
          {!loading && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Upload Portal
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                sx={{ textTransform: 'none' }}
                onClick={handleUploadDocument}
              >
                New Upload
              </Button>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: 'grey.50' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                    <Build sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Inspection Reports
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {uploadStats?.inspectionReports.description || 'Loading...'}
                    </Typography>
                  </Box>
                  <Button variant="outlined" size="small" fullWidth onClick={handleUploadDocument}>
                    Upload
                  </Button>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: 'grey.50' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                    <GpsFixed sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      GPS Installation
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {uploadStats?.gpsInstallation.description || 'Loading...'}
                    </Typography>
                  </Box>
                  <Button variant="outlined" size="small" fullWidth onClick={handleUploadDocument}>
                    Upload
                  </Button>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: 'grey.50' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                    <Description sx={{ fontSize: 48, color: 'info.main', mb: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Documentation
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {uploadStats?.documentation.description || 'Loading...'}
                    </Typography>
                  </Box>
                  <Button variant="outlined" size="small" fullWidth onClick={handleUploadDocument}>
                    Upload
                  </Button>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: 'grey.50' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                    <Security sx={{ fontSize: 48, color: 'warning.main', mb: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Insurance Verification
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {uploadStats?.insuranceVerification.description || 'Loading...'}
                    </Typography>
                  </Box>
                  <Button variant="outlined" size="small" fullWidth onClick={handleUploadDocument}>
                    Upload
                  </Button>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={3}>
        {/* Recent Uploads */}
        <Grid item xs={12} lg={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Recent Uploads
              </Typography>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Partner</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Document</TableCell>
                      <TableCell>Vehicle</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {uploadsLoading ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : uploads.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No uploads found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      uploads.map((upload) => (
                        <TableRow key={upload.id} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Avatar sx={{ width: 32, height: 32 }}>
                                {upload.partner.charAt(0)}
                              </Avatar>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {upload.partner}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={getServiceIcon(upload.type)}
                              label={upload.type}
                              size="small"
                              color={getServiceColor(upload.type) as any}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{upload.document}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{upload.vehicle}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {upload.date}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={getStatusIcon(upload.status)}
                              label={upload.status}
                              size="small"
                              color={getStatusColor(upload.status) as any}
                              variant={upload.status === 'Verified' ? 'filled' : 'outlined'}
                            />
                          </TableCell>
                          <TableCell>
                            <Tooltip title="View Details">
                              <IconButton size="small">
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Review History */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Review History
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="outlined" size="small" startIcon={<FilterList />}>
                    Filter
                  </Button>
                  <Button variant="outlined" size="small" startIcon={<Download />}>
                    Export
                  </Button>
                </Box>
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Partner</TableCell>
                      <TableCell>Service Type</TableCell>
                      <TableCell>Vehicle</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Rating</TableCell>
                      <TableCell>Feedback</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reviewsLoading ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : reviews.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No reviews found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      reviews.map((review) => (
                        <TableRow key={review.id} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {review.partner}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={getServiceIcon(review.serviceType)}
                              label={review.serviceType}
                              size="small"
                              color={getServiceColor(review.serviceType) as any}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{review.vehicle}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {review.date}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Rating value={review.rating} readOnly size="small" />
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {review.feedback}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <IconButton size="small">
                              <MoreVert fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Button variant="outlined" size="small">
                  Load More Reviews
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Partner Trust Ratings */}
        <Grid item xs={12} lg={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Partner Trust Ratings
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="text" size="small">Filter</Button>
                  <Button variant="text" size="small">Export</Button>
                </Box>
              </Box>

              {partners.map((partner) => (
                <Card key={partner.id} sx={{ mb: 2, backgroundColor: 'grey.50' }}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ width: 40, height: 40 }}>
                        {partner.name.charAt(0)}
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {partner.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {partner.type}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {partner.trustRating.overall}
                        </Typography>
                        <Rating value={partner.trustRating.overall} readOnly size="small" />
                      </Box>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption">Accuracy</Typography>
                        <Typography variant="caption">{partner.trustRating.accuracy}/5</Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={(partner.trustRating.accuracy / 5) * 100}
                        sx={{ height: 4, borderRadius: 2 }}
                      />
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption">Timeliness</Typography>
                        <Typography variant="caption">{partner.trustRating.timeliness}/5</Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={(partner.trustRating.timeliness / 5) * 100}
                        sx={{ height: 4, borderRadius: 2 }}
                      />
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption">Documentation</Typography>
                        <Typography variant="caption">{partner.trustRating.documentation}/5</Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={(partner.trustRating.documentation / 5) * 100}
                        sx={{ height: 4, borderRadius: 2 }}
                      />
                    </Box>

                    <Typography variant="caption" color="text.secondary">
                      {partner.completedJobs} {partner.serviceType.toLowerCase()}s completed
                    </Typography>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                      <Button variant="text" size="small" startIcon={<History />}>
                        View History
                      </Button>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, partner.id)}
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => selectedPartnerId && handleViewPartner(selectedPartnerId)}>
          <Visibility sx={{ mr: 1 }} /> View Profile
        </MenuItem>
        <MenuItem onClick={() => selectedPartnerId && handleEditPartner(selectedPartnerId)}>
          <Edit sx={{ mr: 1 }} /> Edit Partner
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <History sx={{ mr: 1 }} /> View History
        </MenuItem>
        <MenuItem
          onClick={() => selectedPartnerId && handleDeletePartner(selectedPartnerId)}
          sx={{ color: 'error.main' }}
        >
          <Delete sx={{ mr: 1 }} /> Delete Partner
        </MenuItem>
      </Menu>

      {/* Dialogs */}
      <AddEditPartnerDialog
        open={addEditDialogOpen}
        onClose={() => setAddEditDialogOpen(false)}
        onSave={handleSavePartner}
        partner={editingPartner}
        loading={actionLoading}
      />

      <UploadDocumentDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onUpload={handleSaveUpload}
        partners={partners}
        loading={actionLoading}
      />

      <PartnerFilterDialog
        open={filterDialogOpen}
        onClose={() => setFilterDialogOpen(false)}
        onApply={handleApplyFilters}
        initialFilters={filters}
      />

      <PartnerDetailDialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        partner={viewingPartner}
        onEdit={() => {
          setDetailDialogOpen(false);
          if (viewingPartner) {
            setEditingPartner(viewingPartner);
            setAddEditDialogOpen(true);
          }
        }}
      />
        </>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};